import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface LanguageState {
  language: string;
  setLanguage: (language: string) => void;
}

// Helper to get browser language
export const getBrowserLanguage = (): string => {
  try {
    const browserLang = navigator.language.split('-')[0].toLowerCase();
    const supportedLangs = ['pl', 'en', 'de', 'es', 'fr', 'it', 'cz', 'sk', 'ua', 'ru', 'gr', 'se', 'no'];
    return supportedLangs.includes(browserLang) ? browserLang : 'en';
  } catch {
    return 'en';
  }
};

// Get initial language - check localStorage (manual save), user meta, then browser
const getInitialLanguage = (): string => {
  // 1. Check if user manually saved language (preAuthLanguage)
  try {
    const preAuth = localStorage.getItem('preAuthLanguage');
    if (preAuth) {
      return preAuth;
    }
  } catch {
    // ignore
  }

  // 2. Check old language-storage for backward compatibility
  try {
    const stored = localStorage.getItem('language-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.state?.language && parsed.state.language !== 'en') {
        // Only use stored language if it's not the default 'en'
        return parsed.state.language;
      }
    }
  } catch {
    // ignore
  }

  // 3. Use browser language
  return getBrowserLanguage();
};

export const useLanguageState = create<LanguageState>()(
  persist(
    (set) => ({
      language: getInitialLanguage(),
      setLanguage: (language: string) => set({ language }),
    }),
    {
      name: 'language-storage',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
